import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { financeApi } from "./finance-api";
import type { InvoiceFilter, NewInvoicePayload } from "./types";

const KEY = ["finance"];

export const useFinanceDashboard = () =>
	useQuery({ queryKey: [...KEY, "dashboard"], queryFn: financeApi.dashboard });

export const useInvoices = (filter: InvoiceFilter = {}) =>
	useQuery({
		queryKey: [...KEY, "invoices", filter],
		queryFn: () => financeApi.invoices(filter),
		placeholderData: (p) => p,
	});

export const useInvoice = (id: string | undefined) =>
	useQuery({
		queryKey: [...KEY, "invoice", id],
		queryFn: () => financeApi.invoice(id!),
		enabled: !!id,
	});

export function useIssueInvoice() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (p: NewInvoicePayload) => financeApi.issueInvoice(p),
		onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, "invoices"] }),
	});
}

export function useMarkPaid(id: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (amount: number) => financeApi.markPaid(id, amount),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: [...KEY, "invoice", id] });
			qc.invalidateQueries({ queryKey: [...KEY, "invoices"] });
		},
	});
}

export function useCreateCreditNote() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (p: {
			invoiceId: string;
			reason: string;
			reasonDetail?: string;
			totalHt: number;
			totalVat: number;
			refundMethod?: string;
		}) => financeApi.createCreditNote(p),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: [...KEY, "credit-notes"] });
			qc.invalidateQueries({ queryKey: [...KEY, "invoices"] });
		},
	});
}

export const useCreditNotes = () =>
	useQuery({
		queryKey: [...KEY, "credit-notes"],
		queryFn: financeApi.creditNotes,
	});

export const useJournals = () =>
	useQuery({ queryKey: [...KEY, "journals"], queryFn: financeApi.journals });
export const useJournalEntries = (
	code: string | undefined,
	periodId?: string,
) =>
	useQuery({
		queryKey: [...KEY, "journal-entries", code, periodId],
		queryFn: () => financeApi.journalEntries(code!, periodId),
		enabled: !!code,
	});
export const useEntry = (id: string | undefined) =>
	useQuery({
		queryKey: [...KEY, "entry", id],
		queryFn: () => financeApi.entry(id!),
		enabled: !!id,
	});

export function usePostManualEntry() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (p: {
			postedAt: string;
			label: string;
			reference?: string;
			lines: Array<{
				account: string;
				debit?: number;
				credit?: number;
				label?: string;
			}>;
		}) => financeApi.postManualEntry(p),
		onSuccess: () =>
			qc.invalidateQueries({ queryKey: [...KEY, "journal-entries"] }),
	});
}

export function useReverseEntry() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (v: { id: string; reason: string }) =>
			financeApi.reverseEntry(v.id, v.reason),
		onSuccess: () =>
			qc.invalidateQueries({ queryKey: [...KEY, "journal-entries"] }),
	});
}

export const useChartOfAccounts = () =>
	useQuery({
		queryKey: [...KEY, "chart"],
		queryFn: financeApi.chartOfAccounts,
	});
export const useLedger = (code: string | undefined) =>
	useQuery({
		queryKey: [...KEY, "ledger", code],
		queryFn: () => financeApi.ledger(code!),
		enabled: !!code,
	});

export const useTrialBalance = (periodId?: string) =>
	useQuery({
		queryKey: [...KEY, "trial-balance", periodId],
		queryFn: () => financeApi.trialBalance(periodId),
	});
export const usePeriods = (fiscalYear?: number) =>
	useQuery({
		queryKey: [...KEY, "periods", fiscalYear],
		queryFn: () => financeApi.periods(fiscalYear),
	});

export function useLockPeriod() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => financeApi.lockPeriod(id),
		onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, "periods"] }),
	});
}
export function useUnlockPeriod() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (v: { id: string; reason: string }) =>
			financeApi.unlockPeriod(v.id, v.reason),
		onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, "periods"] }),
	});
}

export const useVatReturns = () =>
	useQuery({
		queryKey: [...KEY, "vat-returns"],
		queryFn: financeApi.vatReturns,
	});
export const useVatPreview = (periodId: string | undefined) =>
	useQuery({
		queryKey: [...KEY, "vat-preview", periodId],
		queryFn: () => financeApi.vatPreview(periodId!),
		enabled: !!periodId,
	});
export function useVatBuild() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (periodId: string) => financeApi.vatBuild(periodId),
		onSuccess: () =>
			qc.invalidateQueries({ queryKey: [...KEY, "vat-returns"] }),
	});
}
export function useVatSubmit() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (v: { id: string; dgiReceiptNo?: string }) =>
			financeApi.vatSubmit(v.id, v.dgiReceiptNo),
		onSuccess: () =>
			qc.invalidateQueries({ queryKey: [...KEY, "vat-returns"] }),
	});
}

export function useRunExport() {
	return useMutation({
		mutationFn: (p: {
			format: string;
			periodFrom: string;
			periodTo: string;
			journals?: string[];
		}) => financeApi.runExport(p),
	});
}

export const useBudgets = (fiscalYear?: number) =>
	useQuery({
		queryKey: [...KEY, "budgets", fiscalYear],
		queryFn: () => financeApi.budgets(fiscalYear),
	});

export const useAuditLog = () =>
	useQuery({
		queryKey: [...KEY, "audit-log"],
		queryFn: () => financeApi.auditLog(100),
	});
export const useAnomalies = () =>
	useQuery({ queryKey: [...KEY, "anomalies"], queryFn: financeApi.anomalies });
